import { nanoid } from 'nanoid';
import { type Dispatch, type SetStateAction, useCallback, useEffect, useMemo, useRef } from 'react';

import { useUser } from '@/hooks';
import useNoodleApi from '@/hooks/useNoodleApi';
import { SocketMessageType } from '@/providers/Socket/SocketContext';
import useSocketContext from '@/providers/Socket/useSocketContext';
import * as tsClient from '@/tsClient';

import { type ActiveUser } from './QuestionnaireContext';
import useQuestionnaireContext from './useQuestionnaireContext';

type Props = {
  currentSection: string;
  questionnaireId: string;
  onChangeActiveUsers: Dispatch<SetStateAction<ActiveUser[]>>;
};

const HEARTBEAT_INTERVAL = 30000; // 30 seconds
const HEARTBEAT_TIMEOUT = 75000; // 75 seconds
const EXPLICIT_EXIT_TIMEOUT = 10000; // 10 seconds for explicit exits to be processed
const CLEANUP_INTERVAL = 45000; // 45 seconds (1.5x heartbeat interval)

const HandleQuestionnaireActivity = ({ currentSection, questionnaireId, onChangeActiveUsers }: Props): null => {
  const [user] = useUser();
  const { getData: broadcastSocketMessage } = useNoodleApi(tsClient.broadcastSocketMessage);
  const { getQuestionnairePerson } = useQuestionnaireContext();
  const {
    isInitialized: isSocketInitialized,
    addListener: addSocketListener,
    removeListener: removeSocketListener,
  } = useSocketContext();
  const mySessionId = useMemo(() => nanoid(), []);

  // Generic user management functions
  const addOrUpdateUser = useCallback(
    (newUser: ActiveUser) => {
      onChangeActiveUsers((prev) => {
        const existingIndex = prev.findIndex((u) => u.sessionId === newUser.sessionId);
        if (existingIndex === -1) {
          return [...prev, newUser];
        }
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], ...newUser };
        return updated;
      });
    },
    [onChangeActiveUsers],
  );

  const removeUsersByCondition = useCallback(
    (condition: (user: ActiveUser) => boolean, _reason: ActiveUser['removalReason']) => {
      onChangeActiveUsers((prev) =>
        prev.filter((activeUser: ActiveUser) => {
          if (condition(activeUser)) {
            return false;
          }
          return true;
        }),
      );
    },
    [onChangeActiveUsers],
  );

  const removeUserBySessionId = useCallback(
    (sessionId: string, reason: ActiveUser['removalReason']) => {
      removeUsersByCondition((activeUser: ActiveUser) => activeUser.sessionId === sessionId, reason);
    },
    [removeUsersByCondition],
  );

  const removeUsersByPersonId = useCallback(
    (personId: string, reason: ActiveUser['removalReason']) => {
      removeUsersByCondition((activeUser: ActiveUser) => activeUser.id === personId, reason);
    },
    [removeUsersByCondition],
  );

  const updateUserActivity = useCallback(
    (sessionId: string, sectionId?: string) => {
      onChangeActiveUsers((prev) => {
        const existingIndex = prev.findIndex((u) => u.sessionId === sessionId);
        if (existingIndex !== -1) {
          const updated = [...prev];
          updated[existingIndex] = {
            ...updated[existingIndex],
            lastSeen: Date.now(),
            ...(sectionId && { sectionId }),
          };
          return updated;
        }
        return prev;
      });
    },
    [onChangeActiveUsers],
  );

  const messageMetadataRef = useRef({
    sectionId: currentSection,
    sessionId: mySessionId,
  });
  messageMetadataRef.current.sectionId = currentSection;
  messageMetadataRef.current.sessionId = mySessionId;

  useEffect(() => {
    if (isSocketInitialized && user?.id && questionnaireId) {
      broadcastSocketMessage({
        channel: `questionnaire/user-activity/${questionnaireId}`,
        data: {
          event: 'joined',
          fieldTemplateKey: null,
          personId: user?.id,
          questionnaireId,
          sectionId: null,
          sessionId: messageMetadataRef.current.sessionId,
        },
        type: SocketMessageType.QUESTIONNAIRE_USER_ACTIVITY_UPDATED,
      });
    }
  }, [isSocketInitialized, questionnaireId, user?.id, broadcastSocketMessage]);

  // Heartbeat system
  useEffect((): (() => void) | undefined => {
    if (!isSocketInitialized || !user?.id || !questionnaireId) return undefined;

    const sendHeartbeat = (): void => {
      broadcastSocketMessage({
        channel: `questionnaire/user-activity/${questionnaireId}`,
        data: {
          event: 'heartbeat',
          fieldTemplateKey: null,
          personId: user.id,
          questionnaireId,
          sectionId: messageMetadataRef.current.sectionId,
          sessionId: messageMetadataRef.current.sessionId,
        },
        type: SocketMessageType.QUESTIONNAIRE_USER_ACTIVITY_UPDATED,
      });
    };

    // Send heartbeat every 30 seconds
    const heartbeatInterval = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);

    return () => {
      clearInterval(heartbeatInterval);
    };
  }, [isSocketInitialized, questionnaireId, user?.id, broadcastSocketMessage]);

  // Generic cleanup system with configurable timeouts
  useEffect((): (() => void) => {
    const cleanupUsers = (): void => {
      const now = Date.now();

      removeUsersByCondition((activeUser: ActiveUser) => {
        const timeSinceLastSeen = now - activeUser.lastSeen;

        // Remove users based on different criteria
        if (activeUser.removalReason === 'explicit_exit' && timeSinceLastSeen > EXPLICIT_EXIT_TIMEOUT) {
          return true; // Remove users marked for explicit exit after grace period
        }

        if (timeSinceLastSeen > HEARTBEAT_TIMEOUT) {
          return true; // Remove users who haven't sent heartbeat
        }

        return false;
      }, 'timeout');
    };

    // Run cleanup every 45 seconds
    const cleanupInterval = setInterval(cleanupUsers, CLEANUP_INTERVAL);

    return () => {
      clearInterval(cleanupInterval);
    };
  }, [removeUsersByCondition]);

  useEffect(() => {
    if (isSocketInitialized && currentSection && user?.id && questionnaireId) {
      broadcastSocketMessage({
        channel: `questionnaire/user-activity/${questionnaireId}`,
        data: {
          event: 'entered',
          fieldTemplateKey: null,
          personId: user?.id,
          questionnaireId,
          sectionId: messageMetadataRef.current.sectionId,
          sessionId: messageMetadataRef.current.sessionId,
        },
        type: SocketMessageType.QUESTIONNAIRE_USER_ACTIVITY_UPDATED,
      });
    }
  }, [currentSection, isSocketInitialized, questionnaireId, user?.id, broadcastSocketMessage]);

  useEffect((): (() => void) | undefined => {
    if (!questionnaireId) {
      return undefined;
    }

    const listenerId = addSocketListener({
      fn: async (data: unknown) => {
        const {
          personId,
          event,
          sectionId,
          sessionId,
          questionnaireId: eventQuestionnaireId,
        } = data as {
          event: string;
          personId: string;
          questionnaireId?: string;
          sectionId: string;
          sessionId: string;
        };

        // Skip our own events
        if (sessionId === messageMetadataRef.current.sessionId) return;

        // Validate this event is for our questionnaire
        if (eventQuestionnaireId && eventQuestionnaireId !== questionnaireId) {
          return;
        }
        if (event === 'entered') {
          const person = await getQuestionnairePerson(personId);
          if (person) {
            // Add new user session (allow multiple sessions per user)
            const newActiveUser: ActiveUser = {
              color: person.color,
              fieldTemplateKey: null,
              id: person.id,
              lastSeen: Date.now(),
              logo: person.url,
              name: person.name,
              sectionId,
              sessionId,
            };
            addOrUpdateUser(newActiveUser);
          }
        }
        if (event === 'heartbeat') {
          // Update user activity timestamp
          updateUserActivity(sessionId, sectionId);
        }
        if (event === 'joined') {
          broadcastSocketMessage({
            channel: `questionnaire/user-activity/${questionnaireId}`,
            data: {
              event: 'entered',
              fieldTemplateKey: null,
              personId: user?.id,
              questionnaireId,
              sectionId: messageMetadataRef.current.sectionId,
              sessionId: messageMetadataRef.current.sessionId,
            },
            type: SocketMessageType.QUESTIONNAIRE_USER_ACTIVITY_UPDATED,
          });
        }
        if (event === 'exited') {
          // Mark user for removal with explicit exit reason
          removeUserBySessionId(sessionId, 'explicit_exit');
        }
      },
      messageType: SocketMessageType.QUESTIONNAIRE_USER_ACTIVITY_UPDATED,
    });
    return (): void => {
      removeSocketListener(listenerId);
    };
  }, [
    addSocketListener,
    removeSocketListener,
    user?.id,
    questionnaireId,
    isSocketInitialized,
    broadcastSocketMessage,
    getQuestionnairePerson,
    addOrUpdateUser,
    removeUsersByPersonId,
    removeUserBySessionId,
    updateUserActivity,
  ]);

  useEffect((): (() => void) => {
    const sendExitMessage = (): void => {
      if (isSocketInitialized && user?.id && questionnaireId) {
        broadcastSocketMessage({
          channel: `questionnaire/user-activity/${questionnaireId}`,
          data: {
            event: 'exited',
            fieldTemplateKey: null,
            personId: user.id,
            questionnaireId,
            sectionId: null,
            sessionId: messageMetadataRef.current.sessionId,
          },
          type: SocketMessageType.QUESTIONNAIRE_USER_ACTIVITY_UPDATED,
        });
      }
    };

    const handleBeforeUnload = (): void => {
      sendExitMessage();
    };

    // Try multiple approaches to send exit message
    window.addEventListener('beforeunload', handleBeforeUnload);

    return (): void => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      sendExitMessage();
    };
  }, [user?.id, broadcastSocketMessage, questionnaireId, isSocketInitialized]);

  return null;
};

export default HandleQuestionnaireActivity;
